import { useSelector } from '@tanstack/react-store';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { ColumnsIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../../buttons/button/Button.js';
import { Tooltip } from '../../../../../overlays/tooltip/Tooltip.js';
import {
  useDataTable,
  useDataTableColumnSettings,
} from '../../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../../messages.js';
import type { DataTableColumnSettingsTriggerProps } from './column-settings-types.js';

interface ColumnSettingsProps {
  customTriggerProps?: DataTableColumnSettingsTriggerProps;
}

/**
 * DataTable column settings. Internally used component containing a trigger button
 * that opens the column settings modal which displays a list of all columns.
 * @internal
 */
export const DataTableColumnSettings = (
  props: ColumnSettingsProps,
): JSX.Element => {
  const { customTriggerProps } = props;
  const {
    children: customTrigger,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  } = customTriggerProps ?? {};

  const intl = useIntl();
  const table = useDataTable();
  const columnSettingsModal = useDataTableColumnSettings();
  const hiddenColumnCount = useSelector(
    table.atoms.columnVisibility,
    () =>
      table
        .getAllLeafColumnsWithoutBuiltin()
        .filter((column) => !column.getIsVisible()).length,
  );

  const buttonText =
    hiddenColumnCount === 0
      ? intl.formatMessage(dataTableMessages.columnSettingsButtonDefault)
      : intl.formatMessage(dataTableMessages.columnSettingsButtonVisibility, {
          hiddenColumnCount,
        });

  return (
    <Tooltip text={intl.formatMessage(dataTableMessages.columnSettingsTooltip)}>
      <Button
        onClick={columnSettingsModal?.openColumnSettings}
        variant="default"
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        {...remainingProps}
        data-dt-children-name="OpenColumnSettings"
      >
        <Button.Prefix>
          <ColumnsIcon />
        </Button.Prefix>
        {customTrigger
          ? customTrigger({
              hiddenColumnCount,
              totalColumnCount: table.getAllColumns().length,
              defaultLabel: buttonText,
            })
          : buttonText}
      </Button>
    </Tooltip>
  );
};
